/**
 * Collection Filter Manager
 * Handles AJAX filtering, URL state management, infinite scroll, and load more
 */

class CollectionFilterManager {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      ajaxEnabled: true,
      paginationType: 'numbers', // numbers | load_more | infinite
      sectionId: container.dataset.sectionId || 'main-collection',
      loadingClass: 'is-loading',
      ...options
    };

    // Cache DOM elements
    this.filterForm = container.querySelector('[data-filter-form]');
    this.productGrid = container.querySelector('[data-product-grid]');
    this.productCount = container.querySelector('[data-product-count]');
    this.sortSelect = container.querySelector('[data-sort-select]');
    this.pagination = container.querySelector('[data-pagination]');
    this.activeFilters = container.querySelector('[data-active-filters]');
    this.loadMoreBtn = container.querySelector('[data-load-more]');
    this.filterDrawer = container.querySelector('[data-filters]');
    this.filterToggle = container.querySelectorAll('[data-filter-toggle]');

    this.currentUrl = new URL(window.location);
    this.isLoading = false;
    this.scrollObserver = null;

    this.init();
  }

  init() {
    if (!this.container) return;

    this.bindEvents();
    this.setupPagination();

    // Publish init event
    if (window.Storefront?.pubsub) {
      window.Storefront.pubsub.publish('collection:init', { container: this.container });
    }
  }

  bindEvents() {
    // Filter form change (checkboxes, radios)
    this.filterForm?.addEventListener('change', this.handleFilterChange.bind(this));

    // Sort change
    this.sortSelect?.addEventListener('change', this.handleSortChange.bind(this));

    // Price inputs (debounced)
    const priceInputs = this.container.querySelectorAll('[data-price-input]');
    priceInputs.forEach(input => {
      input.addEventListener('input', this.debounce(this.handlePriceChange.bind(this), 500));
    });

    // Price slider
    this.container.addEventListener('price-slider:change', this.handlePriceSliderChange.bind(this));

    // Clear filters
    this.container.querySelectorAll('[data-clear-filters]').forEach(btn => {
      btn.addEventListener('click', this.handleClearFilters.bind(this));
    });

    // Remove single filter
    this.container.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-filter]');
      if (removeBtn) {
        this.handleRemoveFilter(removeBtn);
      }
    });

    // Load more button
    this.loadMoreBtn?.addEventListener('click', this.loadMore.bind(this));

    // Filter drawer toggle
    this.filterToggle.forEach(toggle => {
      toggle.addEventListener('click', this.toggleFilterDrawer.bind(this));
    });

    // Close drawer on overlay click
    this.container.querySelector('[data-filter-overlay]')?.addEventListener('click', () => {
      this.closeFilterDrawer();
    });

    // Close drawer on close button click
    this.container.querySelectorAll('[data-filter-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeFilterDrawer());
    });

    // Browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState.bind(this));

    // Submit handler for non-AJAX fallback
    this.filterForm?.addEventListener('submit', (e) => {
      if (this.options.ajaxEnabled) {
        e.preventDefault();
        this.applyFilters();
      }
    });
  }

  setupPagination() {
    if (this.options.paginationType === 'infinite') {
      this.setupInfiniteScroll();
    }
  }

  setupInfiniteScroll() {
    const trigger = this.container.querySelector('[data-infinite-trigger]');
    if (!trigger) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading) {
          this.loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    this.scrollObserver.observe(trigger);
  }

  // Event Handlers
  handleFilterChange(e) {
    if (!this.options.ajaxEnabled) return;

    // Don't process if it's a price input (handled separately)
    if (e.target.matches('[data-price-input]')) return;

    e.preventDefault();
    this.applyFilters();
  }

  handleSortChange(e) {
    if (!this.options.ajaxEnabled) {
      // Traditional form submit for non-AJAX
      const form = this.sortSelect.closest('form');
      if (form) form.submit();
      return;
    }

    this.applyFilters();
  }

  handlePriceChange(e) {
    if (!this.options.ajaxEnabled) return;
    this.applyFilters();
  }

  handlePriceSliderChange(e) {
    if (!this.options.ajaxEnabled) return;

    // Hidden inputs are already set in cents by the inline slider JS
    this.applyFilters();
  }

  handleClearFilters(e) {
    e.preventDefault();

    // Reset form
    if (this.filterForm) {
      this.filterForm.reset();

      // Clear all checkboxes
      this.filterForm.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.checked = false;
      });

      // Reset price inputs
      this.filterForm.querySelectorAll('[data-price-input]').forEach(input => {
        input.value = '';
      });
    }

    // Navigate to clean URL
    const cleanUrl = new URL(window.location.pathname, window.location.origin);

    // Preserve sort if exists
    const sort = this.currentUrl.searchParams.get('sort_by');
    if (sort) cleanUrl.searchParams.set('sort_by', sort);

    if (this.options.ajaxEnabled) {
      this.fetchAndUpdate(cleanUrl);
    } else {
      window.location.href = cleanUrl.toString();
    }
  }

  handleRemoveFilter(btn) {
    const filterParam = btn.dataset.removeFilter;
    const filterValue = btn.dataset.filterValue;

    if (!filterParam) return;

    const url = new URL(window.location);

    if (filterValue) {
      // Remove specific value from multi-value filter
      const values = url.searchParams.getAll(filterParam);
      url.searchParams.delete(filterParam);
      values.filter(v => v !== filterValue).forEach(v => {
        url.searchParams.append(filterParam, v);
      });
    } else {
      // Remove entire filter parameter
      url.searchParams.delete(filterParam);
    }

    if (this.options.ajaxEnabled) {
      this.fetchAndUpdate(url);
    } else {
      window.location.href = url.toString();
    }
  }

  handlePopState(e) {
    if (!this.options.ajaxEnabled) return;

    this.currentUrl = new URL(window.location);
    this.fetchAndUpdate(this.currentUrl, false); // Don't push state on popstate
  }

  // Main filter application
  async applyFilters() {
    if (this.isLoading) return;

    const url = this.buildFilterUrl();
    await this.fetchAndUpdate(url);
  }

  buildFilterUrl() {
    const url = new URL(window.location.pathname, window.location.origin);
    const formData = new FormData(this.filterForm);

    // Add form data to URL
    for (const [key, value] of formData.entries()) {
      if (value && value.trim() !== '') {
        url.searchParams.append(key, value);
      }
    }

    // Add sort if selected
    if (this.sortSelect?.value) {
      url.searchParams.set('sort_by', this.sortSelect.value);
    }

    return url;
  }

  async fetchAndUpdate(url, pushState = true) {
    this.isLoading = true;
    this.showLoadingState();

    try {
      const html = await this.fetchFilteredContent(url);
      this.updateDOM(html);

      if (pushState) {
        this.updateUrl(url);
      }

      this.currentUrl = url;

      // Reinitialize swatches
      if (window.swatchSystem) {
        window.swatchSystem.reinit();
      }

      // Publish update event
      if (window.Storefront?.pubsub) {
        window.Storefront.pubsub.publish('collection:updated', {
          url: url.toString(),
          count: this.getProductCount()
        });
      }

    } catch (error) {
      console.error('Collection filter error:', error);
      // Fallback to traditional navigation on error
      window.location.href = url.toString();
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  async fetchFilteredContent(url) {
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.options.sectionId);

    const response = await fetch(fetchUrl.toString());

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.text();
  }

  updateDOM(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Update product grid
    const newGrid = doc.querySelector('[data-product-grid]');
    if (newGrid && this.productGrid) {
      this.productGrid.innerHTML = newGrid.innerHTML;
      this.animateProducts();
    }

    // Update product count
    const newCount = doc.querySelector('[data-product-count]');
    if (newCount && this.productCount) {
      this.productCount.innerHTML = newCount.innerHTML;
    }

    // Update pagination
    const newPagination = doc.querySelector('[data-pagination]');
    if (newPagination && this.pagination) {
      this.pagination.innerHTML = newPagination.innerHTML;

      // Rebind load more button
      this.loadMoreBtn = this.pagination.querySelector('[data-load-more]');
      this.loadMoreBtn?.addEventListener('click', this.loadMore.bind(this));

      // Restart infinite scroll observer
      if (this.options.paginationType === 'infinite') {
        this.setupInfiniteScroll();
      }
    }

    // Update active filters
    const newActiveFilters = doc.querySelector('[data-active-filters]');
    if (newActiveFilters && this.activeFilters) {
      this.activeFilters.innerHTML = newActiveFilters.innerHTML;
    }

    // Update filter form state (sync checkboxes)
    this.syncFilterState(doc);

    // Scroll to top of products (optional, can be configured)
    if (this.options.scrollToTop !== false) {
      this.productGrid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  syncFilterState(doc) {
    // Sync checkboxes from new document
    const newForm = doc.querySelector('[data-filter-form]');
    if (!newForm || !this.filterForm) return;

    newForm.querySelectorAll('input[type="checkbox"]').forEach(newCheckbox => {
      const existingCheckbox = this.filterForm.querySelector(
        `input[type="checkbox"][name="${newCheckbox.name}"][value="${newCheckbox.value}"]`
      );
      if (existingCheckbox) {
        existingCheckbox.checked = newCheckbox.checked;
      }
    });
  }

  updateUrl(url) {
    window.history.pushState({ filters: url.search }, '', url.toString());
  }

  // Load more / Infinite scroll
  async loadMore() {
    if (this.isLoading) return;

    const nextLink = this.pagination?.querySelector('[data-next-page]');
    if (!nextLink) return;

    const nextUrl = new URL(nextLink.href);
    this.isLoading = true;

    if (this.loadMoreBtn) {
      this.loadMoreBtn.classList.add('is-loading');
      this.loadMoreBtn.disabled = true;
    }

    try {
      const html = await this.fetchFilteredContent(nextUrl);
      this.appendProducts(html);

    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      this.isLoading = false;

      if (this.loadMoreBtn) {
        this.loadMoreBtn.classList.remove('is-loading');
        this.loadMoreBtn.disabled = false;
      }
    }
  }

  appendProducts(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Get new products
    const newProducts = doc.querySelectorAll('[data-product-grid] > *');
    if (this.productGrid && newProducts.length) {
      newProducts.forEach(product => {
        const clone = product.cloneNode(true);
        clone.style.opacity = '0';
        this.productGrid.appendChild(clone);
      });

      // Animate new products
      this.animateProducts();

      // Reinitialize swatches
      if (window.swatchSystem) {
        window.swatchSystem.reinit();
      }
    }

    // Update pagination
    const newPagination = doc.querySelector('[data-pagination]');
    if (newPagination && this.pagination) {
      this.pagination.innerHTML = newPagination.innerHTML;

      // Rebind load more
      this.loadMoreBtn = this.pagination.querySelector('[data-load-more]');
      this.loadMoreBtn?.addEventListener('click', this.loadMore.bind(this));

      // Update infinite scroll trigger
      if (this.options.paginationType === 'infinite') {
        const newTrigger = this.pagination.querySelector('[data-infinite-trigger]');
        if (newTrigger && this.scrollObserver) {
          this.scrollObserver.disconnect();
          this.scrollObserver.observe(newTrigger);
        }
      }
    }
  }

  // Loading states
  showLoadingState() {
    this.productGrid?.classList.add(this.options.loadingClass);
    this.container.classList.add(this.options.loadingClass);
  }

  hideLoadingState() {
    this.productGrid?.classList.remove(this.options.loadingClass);
    this.container.classList.remove(this.options.loadingClass);
  }

  animateProducts() {
    const products = this.productGrid?.querySelectorAll('[data-product-card]');
    products?.forEach((product, index) => {
      if (product.style.opacity === '0' || !product.classList.contains('is-visible')) {
        setTimeout(() => {
          product.style.opacity = '1';
          product.classList.add('is-visible');
        }, index * 50);
      }
    });
  }

  // Filter drawer
  toggleFilterDrawer() {
    if (this.filterDrawer?.classList.contains('is-open')) {
      this.closeFilterDrawer();
    } else {
      this.openFilterDrawer();
    }
  }

  openFilterDrawer() {
    this.filterDrawer?.classList.add('is-open');
    this.container.querySelector('[data-filter-overlay]')?.classList.add('is-visible');
    document.body.classList.add('filter-drawer-open');

    // Focus trap
    const firstFocusable = this.filterDrawer?.querySelector('button, input, select, a');
    firstFocusable?.focus();

    // Close on escape
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  closeFilterDrawer() {
    this.filterDrawer?.classList.remove('is-open');
    this.container.querySelector('[data-filter-overlay]')?.classList.remove('is-visible');
    document.body.classList.remove('filter-drawer-open');
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      this.closeFilterDrawer();
    }
  }

  // Utility methods
  getProductCount() {
    const countEl = this.productCount?.querySelector('[data-count]');
    return countEl ? parseInt(countEl.textContent, 10) : 0;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Public API
  refresh() {
    this.fetchAndUpdate(this.currentUrl, false);
  }

  destroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    document.removeEventListener('keydown', this.handleEscapeKey);
  }
}

// Price Range Slider Component
class PriceRangeSlider {
  constructor(container) {
    this.container = container;
    this.min = parseFloat(container.dataset.min) || 0;
    this.max = parseFloat(container.dataset.max) || 1000;
    this.currentMin = parseFloat(container.dataset.currentMin) || this.min;
    this.currentMax = parseFloat(container.dataset.currentMax) || this.max;
    this.currency = container.dataset.currency || '$';

    this.minInput = container.querySelector('[data-slider-min]');
    this.maxInput = container.querySelector('[data-slider-max]');
    this.range = container.querySelector('[data-slider-range]');
    this.minDisplay = container.querySelector('[data-price-min-display]');
    this.maxDisplay = container.querySelector('[data-price-max-display]');

    this.init();
  }

  init() {
    if (!this.minInput || !this.maxInput) return;

    // Set initial values
    this.minInput.min = this.min;
    this.minInput.max = this.max;
    this.minInput.value = this.currentMin;

    this.maxInput.min = this.min;
    this.maxInput.max = this.max;
    this.maxInput.value = this.currentMax;

    // Bind events
    this.minInput.addEventListener('input', this.handleMinChange.bind(this));
    this.maxInput.addEventListener('input', this.handleMaxChange.bind(this));
    this.minInput.addEventListener('change', this.emitChange.bind(this));
    this.maxInput.addEventListener('change', this.emitChange.bind(this));

    this.updateDisplay();
    this.updateRange();
  }

  handleMinChange(e) {
    let value = parseFloat(e.target.value);
    const gap = (this.max - this.min) * 0.05; // 5% gap minimum

    if (value > this.currentMax - gap) {
      value = this.currentMax - gap;
      e.target.value = value;
    }

    this.currentMin = value;
    this.updateDisplay();
    this.updateRange();
  }

  handleMaxChange(e) {
    let value = parseFloat(e.target.value);
    const gap = (this.max - this.min) * 0.05;

    if (value < this.currentMin + gap) {
      value = this.currentMin + gap;
      e.target.value = value;
    }

    this.currentMax = value;
    this.updateDisplay();
    this.updateRange();
  }

  updateDisplay() {
    if (this.minDisplay) {
      this.minDisplay.textContent = `${this.currency}${Math.round(this.currentMin)}`;
    }
    if (this.maxDisplay) {
      this.maxDisplay.textContent = `${this.currency}${Math.round(this.currentMax)}`;
    }
  }

  updateRange() {
    if (!this.range) return;

    const minPercent = ((this.currentMin - this.min) / (this.max - this.min)) * 100;
    const maxPercent = ((this.currentMax - this.min) / (this.max - this.min)) * 100;

    this.range.style.left = `${minPercent}%`;
    this.range.style.width = `${maxPercent - minPercent}%`;
  }

  emitChange() {
    this.container.dispatchEvent(new CustomEvent('price-slider:change', {
      bubbles: true,
      detail: {
        min: this.currentMin,
        max: this.currentMax
      }
    }));
  }
}

// Filter Group Collapsible
class FilterGroup {
  constructor(container) {
    this.container = container;
    this.toggle = container.querySelector('[data-filter-group-toggle]');
    this.content = container.querySelector('[data-filter-group-content]');

    this.init();
  }

  init() {
    if (!this.toggle || !this.content) return;

    this.toggle.addEventListener('click', this.handleToggle.bind(this));

    // Set initial state
    if (this.container.dataset.collapsed === 'true') {
      this.collapse(false);
    }
  }

  handleToggle() {
    if (this.container.classList.contains('is-collapsed')) {
      this.expand();
    } else {
      this.collapse();
    }
  }

  expand() {
    this.container.classList.remove('is-collapsed');
    this.content.style.maxHeight = this.content.scrollHeight + 'px';
    this.toggle.setAttribute('aria-expanded', 'true');

    setTimeout(() => {
      this.content.style.maxHeight = 'none';
    }, 300);
  }

  collapse(animate = true) {
    if (animate) {
      this.content.style.maxHeight = this.content.scrollHeight + 'px';
      requestAnimationFrame(() => {
        this.content.style.maxHeight = '0px';
      });
    } else {
      this.content.style.maxHeight = '0px';
    }

    this.container.classList.add('is-collapsed');
    this.toggle.setAttribute('aria-expanded', 'false');
  }
}

// Initialize on DOM ready
function initCollectionFilters() {
  const collectionSection = document.querySelector('[data-collection-section]');

  if (collectionSection) {
    window.collectionFilter = new CollectionFilterManager(collectionSection, {
      ajaxEnabled: collectionSection.dataset.ajaxEnabled === 'true',
      paginationType: collectionSection.dataset.paginationType || 'numbers',
      scrollToTop: collectionSection.dataset.scrollToTop !== 'false'
    });
  }

  // Initialize price sliders
  document.querySelectorAll('[data-price-slider]').forEach(slider => {
    new PriceRangeSlider(slider);
  });

  // Initialize filter groups
  document.querySelectorAll('[data-filter-group]').forEach(group => {
    new FilterGroup(group);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionFilters);
} else {
  initCollectionFilters();
}

// Reinitialize on Shopify section load
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('[data-collection-section]')) {
    initCollectionFilters();
  }
});

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CollectionFilterManager, PriceRangeSlider, FilterGroup };
}
